import type {
  Address,
  AddressInput,
  Personnel,
  PersonnelInput,
} from "@/lib/brokerAccounts";
import type { AddressValue } from "@/lib/address";

// What an account PUT carries, so a save changes only what the broker changed
// (BRK-S223). Since PMS-S551 the PUT merges: a field the body leaves out is
// kept, `null` clears an optional one, and a person left out of `personnel[]`
// is removed. So every loaded person still goes back out (only a deliberate
// Remove drops one), and a part of an address is cleared on purpose with
// `null`, never by leaving it out (`accountAddress`, BRK-S375). Shared by every
// surface that saves an account (AccountForm, the account record page, the WC
// wizard's mailing address).

/** A decimal string from the contract → the number the write side takes.
 *  Anything unparseable is treated as absent. */
export function parseDecimal(value: string | null): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The untouched fields of a loaded person, already in write shape. */
export type CarriedPerson = Omit<
  PersonnelInput,
  | "id"
  | "firstName"
  | "lastName"
  | "title"
  | "email"
  | "phones"
  | "roles"
  | "ownershipPercentage"
  | "spouse"
> & { spouseId?: string };

/** A null on the read is omitted: PMS keeps what the body leaves out
 *  (PMS-S551), so it stays null. SSN and driver's licence number are never
 *  read here: they are write-only, and omitting them keeps what PMS has. */
export function carriedFrom(p: Personnel): CarriedPerson {
  const remuneration = parseDecimal(p.annualRemuneration ?? null);
  const addresses = (p.addresses ?? []).map((a) => ({
    type: a.type,
    isPrimary: a.isPrimary,
    street: a.street,
    city: a.city,
    state: a.state,
    zip: a.zip,
    ...(a.addressLine2 ? { addressLine2: a.addressLine2 } : {}),
    ...(a.county ? { county: a.county } : {}),
  }));
  return {
    ...(p.duties ? { duties: p.duties } : {}),
    ...(p.birthDate ? { birthDate: p.birthDate } : {}),
    ...(p.wcInclusion ? { wcInclusion: p.wcInclusion } : {}),
    ...(remuneration != null ? { annualRemuneration: remuneration } : {}),
    ...(p.maritalStatus ? { maritalStatus: p.maritalStatus } : {}),
    ...(p.driversLicenseState
      ? { driversLicenseState: p.driversLicenseState }
      : {}),
    ...(addresses.length ? { addresses } : {}),
    ...(p.spouseId ? { spouseId: p.spouseId } : {}),
    // PMS-S846: carried so a roster write never drops it.
    ...(p.nonOwnerIndemnitor ? { nonOwnerIndemnitor: true } : {}),
  };
}

/** A carried person back onto the wire. The spouse link goes out only when the
 *  spouse is still in this write: PMS 400s a link to anyone else, and a removed
 *  spouse means the broker ended the link. */
export function carriedInput(
  c: CarriedPerson | undefined,
  rosterIds: ReadonlySet<string>,
): Partial<PersonnelInput> {
  if (!c) return {};
  const { spouseId, ...rest } = c;
  return {
    ...rest,
    ...(spouseId && rosterIds.has(spouseId)
      ? { spouse: { personnelId: spouseId } }
      : {}),
  };
}

/** The address parts the editor that made a value shows: a part it shows
 *  goes out as typed, or `null` once emptied; one it doesn't is left out. */
export interface AddressShown {
  line2: boolean;
  county: boolean;
}

/** The account forms (AccountForm, the account record page) show the county
 *  (BRK-S308) but not line 2. */
export const ACCOUNT_FORM_ADDRESS: AddressShown = {
  line2: false,
  county: true,
};

/**
 * An address as the account PUT takes it (BRK-S375). PMS keeps what the body
 * leaves out and clears what it sends as `null` (PMS-S551), so each optional
 * part goes out by what its editor shows: as typed, or `null` once emptied;
 * a part the editor doesn't show is left out, and PMS keeps it.
 *
 * Line 2 the editor doesn't show (the account forms) rides from the loaded
 * address it still IS (the same street, city and state). Once the address
 * moved away from one that had a line 2, it is cleared: a different address
 * must not keep the old suite.
 */
export function accountAddress(
  sent: AddressValue,
  shown: AddressShown,
  ...loaded: (Address | null | undefined)[]
): AddressInput {
  const typed = (part: string | undefined) => part?.trim() || null;
  const line2 = shown.line2 ? typed(sent.addressLine2) : carriedLine2();
  return {
    street: sent.street.trim(),
    ...(line2 !== undefined ? { addressLine2: line2 } : {}),
    city: sent.city.trim(),
    state: sent.state.trim(),
    zip: sent.zip.trim(),
    ...(shown.county ? { county: typed(sent.county) } : {}),
  };

  function carriedLine2(): string | null | undefined {
    const same = (a: string, b: string) =>
      a.trim().toLowerCase() === b.trim().toLowerCase();
    const match = loaded.find(
      (a) =>
        a &&
        same(a.street, sent.street) &&
        same(a.city, sent.city) &&
        same(a.state, sent.state),
    );
    if (match) return match.addressLine2 || undefined;
    return loaded.some((a) => a?.addressLine2) ? null : undefined;
  }
}
